/**
 * 고객센터 페이지
 */

import Seo from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { HelpCircle, Mail, MessageCircle, Phone } from "lucide-react";
import { useState } from "react";

const faqs = [
  {
    question: "계정을 만들려면 어떻게 해야 하나요?",
    answer:
      "상단 우측의 프로필 아이콘을 클릭하시면 로그인 및 회원가입을 할 수 있습니다.",
  },
  {
    question: "게시글을 작성하려면 어떻게 해야 하나요?",
    answer:
      "로그인 후 상단의 '글쓰기' 버튼을 클릭하시거나, 각 카테고리 페이지에서 '등록하기' 버튼을 클릭하시면 됩니다.",
  },
  {
    question: "광고를 게재하려면 어떻게 해야 하나요?",
    answer: "광고 문의 페이지에서 문의하시거나, 이메일로 직접 문의해주세요.",
  },
  {
    question: "비즈니스를 등록하려면 어떻게 해야 하나요?",
    answer:
      "비즈니스 디렉토리 페이지에서 '등록하기' 버튼을 클릭하시면 등록 양식을 작성하실 수 있습니다.",
  },
];

export default function Support() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(formData.subject || "고객센터 문의");
    const body = encodeURIComponent(
      `이름: ${formData.name}\n이메일: ${formData.email}\n\n문의 내용:\n${formData.message}`
    );
    window.location.href = `mailto:ozknews.com@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <Seo
        title="고객센터 — OZK News"
        description="OZK News 고객센터에 문의하세요."
        path="/support"
      />

      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">고객센터</h1>
            <p className="text-lg text-muted-foreground">
              궁금한 사항이 있으시면 언제든지 문의해주세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  전화 문의
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold mb-2">+61-450 760 790</p>
                <p className="text-sm text-muted-foreground">
                  평일 09:00 - 18:00 (AEST)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  이메일 문의
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold mb-2">
                  ozknews.com@gmail.com
                </p>
                <p className="text-sm text-muted-foreground">
                  24시간 접수 가능
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 문의 양식 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  문의하기
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">이름</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">이메일</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject">제목</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">문의 내용</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      rows={6}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    <Mail className="w-4 h-4 mr-2" />
                    이메일로 문의하기
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* 자주 묻는 질문 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  자주 묻는 질문
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="border-b border-line-subtle pb-4 last:border-0"
                    >
                      <p className="font-semibold mb-2">{faq.question}</p>
                      <p className="text-sm text-muted-foreground">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
